import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Car, Caravan, Truck, Tractor, Ship, Bike, ArrowUpRight, ArrowRight,
  Zap, Wrench, Timer, Gauge, Globe2, Headset,
} from "lucide-react";
import { MaskedLines, Reveal, Marquee, SectionHead, CountUp, PortalButton } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";
import { HomeRegistrationSearch } from "@/components/tuning/HomeRegistrationSearch";
import { API, PORTAL_URL } from "@/lib/site";

const HERO_IMG = "/assets/workshop/dyno-lambo.webp";

const TRUST = [
  ["20+", "Years Experience"],
  ["1,000+", "Dealers"],
  ["4WD", "Dyno Development"],
  ["24/7", "Worldwide Support"],
];

const VEHICLES = [
  [Car, "Cars"], [Caravan, "Vans"], [Truck, "HGV / Commercial"],
  [Tractor, "Agricultural"], [Ship, "Marine"], [Bike, "Motorcycles"],
];

const BENEFITS = [
  [Timer, "Fast turnaround"],
  [Gauge, "Custom calibration"],
  [Headset, "Experienced technical support"],
  [Zap, "Dyno developed"],
  [Globe2, "Worldwide file service"],
];

const BRANDS = [
  { name: "Alientech", cat: "alientech", tag: "KESS3 · K-TAG", img: "/assets/brands/alientech.webp", pos: "center 38%" },
  { name: "AutoTuner", cat: "autotuner", tag: "OBD · Bench · Datalogging", img: "/assets/brands/autotuner.webp", pos: "center 38%" },
  { name: "Magic Motorsport", cat: "magic-motorsport", tag: "FLEX · X17 Evolution", img: "/assets/brands/magic-motorsport.webp", pos: "center 38%" },
  { name: "Dimsport", cat: "dimsport", tag: "New Genius · New Trasdata", img: "/assets/brands/dimsport.webp", pos: "center 38%" },
];

const TOOL_TYPES = ["Master Tools", "Slave Tools", "Subscriptions", "Upgrades", "Accessories", "Cables", "Used Equipment"];

const STEPS = [
  ["01", "Choose Your Tool", "Master or Slave hardware from Alientech, AutoTuner, Magic Motorsport or Dimsport — matched to your workshop."],
  ["02", "Set Up Your Account", "We activate your dealer account and configure your tools, protocols and file portal access."],
  ["03", "Upload Your Customer's File", "Read the ECU, upload the original file through the Regal File Portal with your job notes."],
  ["04", "Receive Your Tuned File", "Our calibrators return your tuned file — checked, tested and ready to flash. Typically within the hour."],
];

const STATS = [
  { value: 20, suffix: "+", label: "Years Experience" },
  { value: 1000, suffix: "+", label: "Dealers Worldwide" },
  { value: 4, suffix: "WD", label: "Dyno Development" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  useEffect(() => {
    axios.get(`${API}/products/featured`).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-end overflow-hidden sm:min-h-screen" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} srcSet="/assets/workshop/dyno-lambo-800.webp 800w, /assets/workshop/dyno-lambo-1200.webp 1200w, /assets/workshop/dyno-lambo.webp 1600w" sizes="100vw" alt="Professional ECU tuning workshop" width="1600" height="1200" decoding="async" className="h-[115%] w-full object-cover" fetchPriority="high" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-void via-void/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/40" />
        <div className="absolute inset-0 dot-grid opacity-60" />
        <motion.div className="container-regal relative z-10 pb-16 pt-36 sm:pb-24" style={{ opacity: fade }}>
          <motion.p
            className="overline-label mb-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05, duration: 0.8 }}
            data-testid="hero-overline"
          >
            Files. Tools. Support.
          </motion.p>
          <MaskedLines
            lines={["Professional", "ECU Tuning"]}
            className="font-display text-5xl sm:text-7xl lg:text-8xl font-extrabold uppercase leading-[0.98] tracking-tight text-ink"
          />
          <Reveal delay={0.55} className="mt-7 max-w-xl">
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed" data-testid="hero-subcopy">
              Professional ECU calibration, genuine tuning equipment and technical support for the worldwide tuning industry.
            </p>
          </Reveal>
          <Reveal delay={0.62}>
            <HomeRegistrationSearch />
          </Reveal>
          <Reveal delay={0.68} className="mt-9 flex flex-wrap items-center gap-4">
            <PortalButton label="Upload a File" testId="hero-upload-file-button" className="px-8 py-4" />
            <Link
              to="/tuning-tools"
              data-testid="hero-buy-tools-button"
              className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-neutral-100 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300"
            >
              Buy Tuning Tools <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={0.8} className="mt-6">
            <p className="text-sm text-neutral-400">
              Already a customer?{" "}
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-portal-link" className="font-semibold text-regal-amber underline-offset-4 hover:underline">
                File Portal <ArrowUpRight className="inline" size={13} />
              </a>
            </p>
          </Reveal>
        </motion.div>
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-line bg-white/80 backdrop-blur-md">
          <div className="container-regal grid grid-cols-2 divide-x divide-line sm:grid-cols-4">
            {TRUST.map(([v, l], i) => (
              <Reveal key={l} delay={0.9 + i * 0.1} y={14} className="px-4 py-5 sm:px-7">
                <p className="font-display text-xl sm:text-2xl font-extrabold text-regal-amber" data-testid={`trust-value-${i}`}>{v}</p>
                <p className="mt-0.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-neutral-400">{l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["ECU Tuning Files", "Genuine Tuning Tools", "Alientech", "AutoTuner", "Magic Motorsport", "Dimsport", "4WD Dyno Developed", "Worldwide Support"]} />

      {/* TUNING FILES */}
      <section className="py-20 sm:py-28" data-testid="tuning-files-section">
        <div className="container-regal">
          <SectionHead
            overline="01 — File Service"
            title="Professional ECU Tuning Files"
            copy="Professional ECU calibration developed and supplied by experienced tuners. Performance, economy and gearbox calibrations for virtually every vehicle on the road."
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {VEHICLES.map(([Icon, label], i) => (
              <Reveal key={label} delay={i * 0.06}>
                <Link
                  to="/tuning-files"
                  data-testid={`vehicle-category-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex h-full flex-col items-center gap-3 border border-line bg-card px-4 py-8 text-center hover:border-regal-amber/60 transition-colors duration-300"
                >
                  <Icon size={26} className="text-neutral-400 group-hover:text-regal-amber transition-colors duration-300" strokeWidth={1.5} />
                  <span className="font-display text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-neutral-200">{label}</span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {BENEFITS.map(([Icon, label]) => (
              <span key={label} className="flex items-center gap-2.5 text-sm text-neutral-300">
                <Icon size={15} className="text-regal-amber" /> {label}
              </span>
            ))}
          </Reveal>
          <Reveal delay={0.22} className="mt-10">
            <PortalButton label="Upload Your File" testId="tuning-files-upload-button" className="px-10 py-4 text-base" />
          </Reveal>
        </div>
      </section>

      {/* TUNING TOOLS */}
      <section className="border-t border-line bg-surface py-20 sm:py-28" data-testid="tuning-tools-section">
        <div className="container-regal">
          <SectionHead
            overline="02 — Equipment"
            title="Professional Tuning Tools"
            copy="Genuine hardware from the industry's leading manufacturers — supplied, configured and supported by Regal Tuning."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BRANDS.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.08}>
                <Link
                  to={b.cat === "autotuner" ? "/autotuner" : b.cat === "alientech" ? "/alientech-kess3" : b.cat === "magic-motorsport" ? "/magicmotorsport-flex" : `/tuning-tools/${b.cat}`}
                  data-testid={`brand-card-${b.cat}`}
                  className="group relative block overflow-hidden border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300"
                >
                  <div className="aspect-square overflow-hidden">
                    <img src={b.img} alt={b.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-regal-amber">{b.tag}</p>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="font-display text-xl font-extrabold uppercase tracking-wide">{b.name}</p>
                      <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-regal-amber transition-colors" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12} className="mt-8 flex flex-wrap gap-2.5">
            {TOOL_TYPES.map((t) => (
              <Link key={t} to="/tuning-tools" data-testid={`tool-type-${t.toLowerCase().replace(/\s+/g, "-")}`} className="border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300">
                {t}
              </Link>
            ))}
          </Reveal>
          {featured.length > 0 && (
            <div className="mt-16">
              <Reveal><p className="overline-label mb-8">Featured Equipment</p></Reveal>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {featured.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 0.06}><ProductCard p={p} /></Reveal>
                ))}
              </div>
            </div>
          )}
          <Reveal delay={0.15} className="mt-12 text-center">
            <Link to="/tuning-tools" data-testid="shop-tools-cta" className="inline-flex items-center gap-2 bg-regal-amber px-10 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void hover:bg-amber-400 transition-colors">
              Shop Tuning Tools <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DEALER */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="dealer-section">
        <div className="container-regal">
          <SectionHead
            overline="03 — Dealer Network"
            title="Start Tuning With Regal"
            copy="Whether you are adding ECU tuning to an existing workshop or starting a new tuning business, Regal Tuning can supply the equipment, software, tuning files and technical support needed to get started."
          />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08} className="bg-void">
                <div className="h-full p-7" data-testid={`dealer-step-${i + 1}`}>
                  <p className="font-mono text-xs tracking-[0.3em] text-regal-amber">{n}</p>
                  <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide">{t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/become-a-dealer" data-testid="become-dealer-cta" className="inline-flex items-center gap-2 border border-regal-amber px-10 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-regal-amber hover:bg-regal-amber hover:text-void transition-colors duration-300">
              Become a Regal Dealer <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="relative overflow-hidden bg-[#0A3D8F]" data-testid="stats-section">
        <img src="/assets/workshop/dyno-turbos.webp" alt="Regal Tuning 4WD dyno development cell" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A3D8F]/70 via-[#0A3D8F]/40 to-[#0A3D8F]/70" />
        <div className="container-regal relative py-24 sm:py-32">
          <Reveal><h2 className="max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight leading-[1.08] text-white">Built from 20+ years of tuning experience</h2></Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1}>
                <p className="font-display text-5xl sm:text-6xl font-extrabold text-white" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CountUp to={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.24em] text-blue-100">{s.label}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="font-display text-5xl sm:text-6xl font-extrabold text-white" data-testid="stat-worldwide">24/7</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.24em] text-blue-100">Worldwide File Support</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="story-section">
        <div className="container-regal grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHead
              overline="04 — Our Story"
              title="From chip tuning to the modern ECU era"
              copy="Regal Tuning has been involved with ECU tuning since the early days, when chips often had to be physically removed and programmed. The business has progressed through every generation of ECU technology and now provides professional tuning files, tuning tools and technical support to customers worldwide."
            />
            <Reveal delay={0.2}>
              <Link to="/about" data-testid="our-story-cta" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-neutral-100 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300">
                Our Story <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden border border-line">
              <img src="/assets/workshop/dyno-911.webp" alt="Porsche 911 on the Regal Tuning dyno" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-void/80 to-transparent" />
              <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.24em] text-neutral-300">Est. 2004 — ECU Calibration Specialists</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PORTAL */}
      <section className="relative overflow-hidden border-t border-regal-amber/30 bg-gradient-to-br from-card via-surface to-void py-20 sm:py-24" data-testid="portal-section">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="container-regal relative flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <Reveal><p className="overline-label mb-4">Existing Customers</p></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight leading-[1.08]">Already a Regal customer?</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">
                Access the Regal Tuning File Portal to upload files, manage tuning jobs and download completed files.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <PortalButton label="Enter File Portal" testId="portal-section-button" className="px-12 py-5 text-base" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
